import { Box, combineSx, Divider, Link, Stack, Typography, useTheme } from '@chronosphereio/chrono-ui';
import { GroupedEventsIcon } from '@chronosphereio/shared';
import { Launch as LaunchIcon } from 'mdi-material-ui';
import { cloneElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { EventGroupPayload, EventItem, EventPayload } from '@/core';

const EVENT_ICON_SIZE = 16;

export function EventTooltip({ event, clearPinned }: { event: EventItem; clearPinned?: () => void }) {
  if ('totalCount' in event) {
    return <EventGroupTooltipContent eventGroup={event} clearPinned={clearPinned} />;
  }
  return <EventTooltipContent event={event} clearPinned={clearPinned} />;
}

export function EventTooltipContent({ event, clearPinned }: { event: EventPayload; clearPinned?: () => void }) {
  const { theme } = useTheme();

  let icon = null;
  const categoryIcon = event.category?.icon;
  if (categoryIcon) {
    icon = cloneElement(categoryIcon, {
      sx: combineSx(categoryIcon.props.sx, { width: EVENT_ICON_SIZE, height: EVENT_ICON_SIZE }),
    });
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center" paddingY={0.5} margin={0}>
        {event.category && (
          <>
            {icon}
            <Typography variant={'body2'} flexGrow={1} fontWeight={700}>
              {event.category.name}
            </Typography>
          </>
        )}
      </Stack>
      <Divider color={theme.palette.border.default} />
      <Box paddingTop={0.5}>
        {event.link ? (
          <Link
            underline="hover"
            to={
              window.location.search
                ? `${window.location.search}&${event.link.queryParams}`
                : `?${event.link.queryParams}`
            }
            component={RouterLink}
            color={theme.palette.text.primary}
            target={event.link.target}
            rel={event.link.target === '_blank' ? 'noopener' : undefined}
            // Clear pin when change events drawer is opened so the tooltip doesn't render on top of the drawer
            onClick={clearPinned}
          >
            <Typography variant="body2" flexGrow={1}>
              <b>{event.link.label}</b>
            </Typography>
            <Typography variant="body2">{event.title}</Typography>
            {event.link.target && (
              <LaunchIcon
                fontSize="inherit"
                sx={{ marginLeft: (theme) => theme.spacing(0.5), position: 'relative', top: '2px' }}
              />
            )}
          </Link>
        ) : (
          <Typography variant={'body2'} flexGrow={1}>
            {event.title}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export function EventGroupTooltipContent({
  eventGroup,
  clearPinned,
}: {
  eventGroup: EventGroupPayload;
  clearPinned?: () => void;
}) {
  const { theme } = useTheme();

  return (
    <Stack spacing={0.5}>
      <Typography variant={'body2'} flexGrow={1} fontWeight={700}>
        {eventGroup.title}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" paddingY={0.5} margin={0}>
        <GroupedEventsIcon
          width={EVENT_ICON_SIZE}
          height={EVENT_ICON_SIZE}
          fill={theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.common.white}
        />
        <Typography variant={'body2'} flexGrow={1}>
          Total
        </Typography>
        {eventGroup.totalCount}
      </Stack>
      <Divider color={theme.palette.border.default} />
      <Stack spacing={1}>
        {eventGroup.eventCategories.map((category: EventGroupPayload['eventCategories'][0], index: number) => (
          <Stack direction="row" alignItems="center" spacing={1} key={index} paddingTop={0.5} margin={0}>
            {category.icon && (
              <Box display="flex" sx={{ svg: { width: EVENT_ICON_SIZE, height: EVENT_ICON_SIZE } }}>
                {cloneElement(category.icon, {
                  sx: combineSx(category.icon.props.sx, {
                    width: EVENT_ICON_SIZE,
                    height: EVENT_ICON_SIZE,
                  }),
                })}
              </Box>
            )}
            <Typography variant={'body2'} flexGrow={1}>
              {category.name}
            </Typography>
            {category.count}
          </Stack>
        ))}
      </Stack>
      {eventGroup.link && (
        <>
          <Divider color={theme.palette.border.default} />
          <Box paddingTop={0.5}>
            <Link
              underline="hover"
              to={
                window.location.search
                  ? `${window.location.search}&${eventGroup.link.queryParams}`
                  : `?${eventGroup.link.queryParams}`
              }
              component={RouterLink}
              color={theme.palette.text.primary}
              target={eventGroup.link.target}
              rel={eventGroup.link.target === '_blank' ? 'noopener' : undefined}
              // Clear pin when change events drawer is opened so the tooltip doesn't render on top of the drawer
              onClick={clearPinned}
            >
              <Typography variant="body2" flexGrow={1}>
                <strong>{eventGroup.link.label}</strong>
              </Typography>
              {eventGroup.link.target && (
                <LaunchIcon
                  fontSize="inherit"
                  sx={{ marginLeft: (theme) => theme.spacing(0.5), position: 'relative', top: '2px' }}
                />
              )}
            </Link>
          </Box>
        </>
      )}
    </Stack>
  );
}
